import { Col, Input, Modal, Row, Select } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useRef, useState, useEffect } from "react";
import styles from "./modals.module.scss";
import SecondaryButton from "../components/Button/secondaryButton";
import Button from "../components/Button";
import { ReactComponent as ImagePlaceholder } from "../images/image-placeholder.svg";
import { useSecureService } from "../store/service";
import { ApiFolderPostRequest } from "../service/apis/FolderApi";
import { UserApi, Configuration } from '../service';
import { message } from "antd";

interface AddNewFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentId?: number;
  projectId?: number;
  onSuccess?: () => void;
  editMode?: boolean; // Edit mode için
  editData?: any; // Edit edilecek veri
}



const userApi = new UserApi(new Configuration());

const AddNewFolderModal = ({ isOpen, onClose, parentId, projectId, onSuccess, editMode = false, editData }: AddNewFolderModalProps) => {
  const [thumbnail, setThumbnail] = useState<any>(null);
  const [title, setTitle] = useState("");
  const [share, setShare] = useState<string[]>([]);
  const [shareOptions, setShareOptions] = useState<{label: string, value: string}[]>([]);
  const [shareLoading, setShareLoading] = useState(false);

  const uploadRef = useRef<HTMLInputElement>(null);
  const { createFolder, editFolder } = useSecureService();

  // Edit mode'da form'u doldur
  useEffect(() => {
    if (editMode && editData) {
      setTitle(editData.name || '');

      
      // Share verisini email'lere dönüştür
      if (editData.share && Array.isArray(editData.share)) {
        const emails = editData.share.map((item: any) => item.email || item).filter((email: any) => typeof email === 'string' && email.trim());
        setShare(emails);
      } else {
        setShare([]);
      }
      
      // Thumbnail edit mode'da değiştirilemez
    }
  }, [editMode, editData, isOpen]);

  const handleClose = () => {
    setTitle("");
    setShare([]);
    
    setThumbnail(null);
    onClose();
  };

  // Modal kapandığında form'u temizle
  useEffect(() => {
    if (!isOpen) {
      setTitle("");
      setShare([]);
      
      setThumbnail(null);
    }
  }, [isOpen]);

  const handleImageClick = () => {
    if (uploadRef.current) {
      uploadRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setThumbnail(e.target.files[0]);
    }
  };

  const handleShareSearch = async (value: string) => {
    if (!value) {
      setShareOptions([]);
      return;
    }
    setShareLoading(true);
    try {
      const response = await userApi.apiUserEmailsGet({ searchTerm: value }, { credentials: 'include' });
      if (response.success && response.data && response.data.emails) {
        setShareOptions(
          response.data.emails
            .filter((email: string) => email && typeof email === 'string' && email.trim())
            .map((email: string) => ({
              label: email,
              value: email
            }))
        );
      } else {
        setShareOptions([]);
      }
    } catch {
      setShareOptions([]);
    } finally {
      setShareLoading(false);
    }
  };

  const handleSave = async () => {
    if (!title) {
      message.error("Please enter folder name.");
      return;
    }

    try {
      if (editMode && editData) {
        // Edit mode
        const editDataToSend = {
          id: editData.id,
          name: title,
          share,
        };
        
        const result = await editFolder(editDataToSend);
        
        if (result && result.success) {
          handleClose();
          onSuccess?.();
        } else {
          message.error("Folder could not be updated.");
        }
      } else {
        // Create mode
        const folderData: ApiFolderPostRequest = {
          name: title,
          parentId,
          projectId,
          thumbnail,
          share
        };

        const result = await createFolder(folderData);

        if (result && result.success) {
          handleClose();
          onSuccess?.();
        } else {
          message.error("Folder could not be created.");
        }
      }
    } catch (error) {
      message.error(editMode ? "An error occurred while updating the folder." : "An error occurred while creating the folder.");
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      title={<div className={styles.modalTitle}>{editMode ? 'Edit Folder' : 'New Folder'}</div>}
      className={styles.modal}
      closable
      closeIcon={<CloseOutlined style={{ color: "var(--app-accent-color)" }} />}
      width={686}
    >
      <Row className={styles.modalRow} gutter={[24, 24]}>
        {/* Left: Thumbnail */}
        <Col xs={24} md={10}>
          <div className={styles.leftSide}>
            <div
              className={styles.thumbnail}
              onClick={handleImageClick}
            >
              {thumbnail ? (
                <img
                  src={URL.createObjectURL(thumbnail)}
                  alt="thumbnail"
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                />
              ) : (
                <div style={{ width: 117, height: 94, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ImagePlaceholder />
                </div>
              )}
              <input
                ref={uploadRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </div>
            <div className={styles.projectTitle} style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8 }}>Folder Title</label>
              <Input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} />
            </div>
          </div>
        </Col>
        {/* Right: Form fields */}
        <Col xs={24} md={14}>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8 }}>Share With</label>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Add email(s)"
              value={share}
              onChange={setShare}
              onSearch={handleShareSearch}
              options={shareOptions}
              filterOption={false}
              notFoundContent={
                <div style={{ color: '#fff', background: '#222', padding: 8, textAlign: 'center', borderRadius: 8 }}>
                  {shareLoading ? "Loading..." : "No email found"}
                </div>
              }
              showSearch
              tagRender={() => <span style={{ display: 'none' }} />}
            />
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {share.filter(email => typeof email === 'string' && email.trim()).map(email => (
                <div
                  key={email}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#363B3F',
                    borderRadius: 20,
                    padding: '4px 12px',
                    color: '#fff',
                    fontWeight: 500
                  }}
                >
                  <span style={{
                    background: 'var(--app-accent-secondary)',
                    borderRadius: '50%',
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 8,
                    fontWeight: 700,
                    fontSize: 16
                  }}>
                    {email && email[0] ? email[0].toUpperCase() : '?'}
                  </span>
                  {email}
                  <span
                    style={{ marginLeft: 8, cursor: 'pointer' }}
                    onClick={() => setShare(share.filter(e => e !== email))}
                  >
                    ×
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
            <Button onClick={handleSave} className={styles.saveButton} style={{ marginRight: 8 }}>
              {editMode ? 'Update' : 'Save'}
            </Button>
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default AddNewFolderModal; 